import React from 'react';
import {bool, func, number, object, oneOf, shape, string} from 'prop-types';
import classNames from 'classnames';
import {curry} from 'ramda';

import {EditableCaption} from '../EditableText';
import Checkbox from '../forms/Checkbox';

import {convertTime, displayDate} from '../../util/date-utils/date';
import {getSmallestUrlOfSize, mediaIcon} from '../../util/media.js';

import styles from './mediaCard.scss';
import VideoNexusIcon from './VideoNexusIcon.jsx';

const SAVE_TO_STORY = 'saveToStory';
const SAVE_TO_STORY_AND_ORIGINAL = 'saveToStoryAndOriginal';
const getMediaStyle = mediaType => 'fa fa-' + mediaIcon(mediaType);
const CardInfo = ({data: {title, width, height}}) => {
  return (
    <div className={styles.cardInfo}>
      <div className={styles.cardHeader}>
        <div data-type='title'>{title}</div>
      </div>

      {width && (
        <div className={classNames(styles.cardViewsSize, styles.cardViews)}>
          {width} × {height}
        </div>
      )}
    </div>
  );
};
CardInfo.propTypes = {
  data: object,
};

const ListCardInfo = ({data: {title, caption}}) => {
  return (
    <div className={styles.listCardInfo}>
      <div className={styles.cardInfoColumn}>{title}</div>
      <div className={styles.cardInfoColumn}>{caption}</div>
    </div>
  );
};
ListCardInfo.propTypes = {
  data: object,
};

export class EditableCardInfo extends React.Component {
  static propTypes = {
    data: shape({
      title: string,
      caption: string,
      height: number,
      uuid: string,
      width: number,
    }),
    onUpdateCaption: func,
    onUpdateTitle: func,
  };

  onUpdateTitle = curry((operation, uuid, text) => {
    this.props.onUpdateTitle({uuid, operation, text});
  });

  onUpdateCaption = curry((operation, uuid, text) => {
    this.props.onUpdateCaption({operation, uuid, text});
  });

  render() {
    const {
      data: {title, caption, uuid},
    } = this.props;
    return (
      <div className={styles.listCardInfo}>
        <EditableCaption
          className={classNames(styles.editableCaption)}
          onSaveToStory={this.onUpdateTitle(SAVE_TO_STORY, uuid)}
          onSaveToStoryAndOriginal={this.onUpdateTitle(SAVE_TO_STORY_AND_ORIGINAL, uuid)}
          defaultValue={title}
        />
        <EditableCaption
          className={classNames(styles.editableCaption)}
          onSaveToStory={this.onUpdateCaption(SAVE_TO_STORY, uuid)}
          onSaveToStoryAndOriginal={this.onUpdateCaption(SAVE_TO_STORY_AND_ORIGINAL, uuid)}
          defaultValue={caption}
        />
      </div>
    );
  }
}

export const CardInfoWrapper = props => {
  if (props.viewStyle !== 'list-view') {
    return <CardInfo {...props} />;
  }
  if (!props.isEditable) {
    return <ListCardInfo {...props} />;
  }
  return <EditableCardInfo {...props} />;
};
CardInfoWrapper.propTypes = {
  isEditable: bool,
  viewStyle: string,
};

const CardStatus = ({data: {status}}) => {
  if (status) {
    return (
      <sinclair-status data-status={status} data-color='blue'>
        {status}
      </sinclair-status>
    );
  }
  return null;
};
CardStatus.propTypes = {
  data: shape({
    status: string,
  }),
};

const ListCardVisual = props => {
  const {
    data: {uuid, status, created, mediaType, duration, width, height, assetType = ''},
    backgroundImage,
    clickHandler,
    draggable,
    onImageDrag,
  } = props;
  
  return (
    <a href='#' className={styles.cardVisual} onClick={clickHandler}>
      <div
        className={styles.cardImg}
        style={backgroundImage}
        data-id={uuid}
        draggable={draggable}
        onDragStart={onImageDrag}>
        <CardStatus {...props} />
        <i className={classNames('fa', {'fa-refresh': status}, 'fa-spin', styles.sdaUploadingSpinner)} />
      </div>

      {created && <div className={classNames(styles.createdDate)}>{displayDate(created)}</div>}

      <div className={styles.sdaIcon}>
        <i className={getMediaStyle(mediaType)} />
        {duration && <span className={styles.duration}> {convertTime(duration)}</span>}
      </div>

      <VideoNexusIcon type="list" assetType={assetType} mediaType={mediaType} />

      {width && (
        <div className={classNames(styles.listViewsSize)}>
          {width} × {height}
        </div>
      )}
    </a>
  );
};
ListCardVisual.propTypes = {
  data: shape({
    uuid: string,
    status: string,
    created: number,
    duration: number,
    mediaType: string,
    height: number,
    width: number,
  }),
  backgroundImage: object,
  clickHandler: func,
  draggable: bool,
  onImageDrag: func,
};

const CardVisual = props => {
  const {
    data: {uuid, status, created, mediaType, duration, assetType = ''},
    backgroundImage,
    clickHandler,
    draggable,
    onImageDrag,
    className,
  } = props;
  
  return (
    <a href='#' className={className} onClick={clickHandler}>
      <div
        className={styles.cardImg}
        style={backgroundImage}
        data-id={uuid}
        draggable={draggable}
        onDragStart={onImageDrag}>
        <CardStatus {...props} />
        <i className={classNames('fa', {'fa-refresh': status}, 'fa-spin', styles.sdaUploadingSpinner)} />
      </div>

      {created && <div className={classNames(styles.createdDate)}>{displayDate(created)}</div>}

      <div className={styles.sdaIcon}>
        <i className={getMediaStyle(mediaType)} />
        {duration && <span className={styles.duration}>{convertTime(duration)}</span>}
      </div>

      <VideoNexusIcon assetType={assetType} mediaType={mediaType} />
    </a>
  );
};
CardVisual.propTypes = {
  data: shape({
    uuid: string,
    status: string,
    created: number,
    duration: number,
    mediaType: string,
    height: number,
    width: number,
  }),
  backgroundImage: object,
  clickHandler: func,
  draggable: bool,
  onImageDrag: func,
  className: string,
};

export const CardVisualWrapper = props => {
  /**
   * Sends some specific data to the Backbone event handler to use
   * @param  {Object} e [event]
   * @returns {null} no return
   */
  const mediaClick = e => {
    e.preventDefault();
    props.mediaClickHandler(e, props.data.uuid);
  };

  return props.viewStyle === 'list-view' ? (
    <ListCardVisual {...props} clickHandler={mediaClick} />
  ) : (
    <CardVisual {...props} clickHandler={mediaClick} />
  );
};
CardVisualWrapper.propTypes = {
  data: shape({
    uuid: string,
  }),
  mediaClickHandler: func,
  viewStyle: oneOf(['thumbnail-large-view', 'thumbnail-medium-view', 'thumbnail-small-view', 'list-view']),
};

export class CardCheckboxWrapper extends React.Component {
  static propTypes = {
    checkComponentBox: func.isRequired,
    data: shape({
      isChecked: bool,
      uuid: string,
    }),
    isCheckable: func.isRequired,
  };

  /**
   * Passes along an isChecked boolean to the parent view in Backbone
   * @returns {null} no return
   */
  mediaCheckboxHandler = (isChecked, event) => {
    this.props.checkComponentBox(!this.props.data.isChecked, this.props.data.uuid, event);
  };

  render() {
    const {
      data: {isChecked},
      isCheckable,
    } = this.props;
    if (isCheckable('package.update-media')) {
      return <Checkbox className={styles.sdaMediaCheckbox} checked={isChecked} onChange={this.mediaCheckboxHandler} />;
    }

    return null;
  }
}

export const CardBody = (Visual = CardVisualWrapper, Box = CardCheckboxWrapper, Info = CardInfoWrapper) => {
  return class extends React.Component {
    static displayName = 'CardBody';

    /**
     * Passes along an isChecked boolean to the parent view in Backbone
     * @returns {null} no return
     */
    mediaCheckboxHandler = () => this.props.checkComponentBox(!this.props.data.isChecked, this.props.data.uuid);

    render() {
      return (
        <div
          className={styles.cardBody}
          onClick={this.props.isCheckable('package.update-media') ? this.mediaCheckboxHandler : undefined}>
          <Visual className={styles.cardVisual} {...this.props} />
          <Box className={styles.sdaMediaCheckbox} {...this.props} />
          <Info {...this.props} />
        </div>
      );
    }
  };
};

export const MediaCardHOC = (Body = CardBody()) => {
  return class extends React.PureComponent {
    static displayName = 'MediaCard';
    static propTypes = {
      className: string,
      data: shape({
        caption: string,
        created: number,
        duration: number,
        height: number,
        isChecked: bool,
        mediaType: string,
        renditions: object,
        thumbUrl: string,
        thumb_url: string,
        title: string,
        uuid: string,
        width: number,
      }),
      viewStyle: oneOf(['thumbnail-large-view', 'thumbnail-medium-view', 'thumbnail-small-view', 'list-view']),
      checkComponentBox: func,
      mediaClickHandler: func,
      isCheckable: func,
      isEditable: bool,
      onUpdateTitle: func,
      onUpdateCaption: func,
      draggable: bool,
      onImageDrag: func,
      onCardDrag: func,
    };

    render() {
      const {
        data,
        className,
        data: {mediaType, thumbUrl, thumb_url: thumbUrl2},
      } = this.props;

      const imgUrl = mediaType === 'image' ? getSmallestUrlOfSize('16x9', data) : thumbUrl || thumbUrl2;
      const bgImg = imgUrl ? {backgroundImage: 'url(' + imgUrl + ')'} : {};

      const viewStyles = {
        'thumbnail-large-view': styles.thumbnailLargeView,
        'thumbnail-medium-view': styles.thumbnailMediumView,
        'thumbnail-small-view': styles.thumbnailSmallView,
        'list-view': styles.listView,
      };

      return (
        <article
          id={data.uuid}
          className={classNames(styles.sdaCardMedia, viewStyles[this.props.viewStyle], data.status, className)}
          data-order={data.orderNumber}
          data-type={data.mediaType}
          key={data.uuid}>
          <Body {...this.props} backgroundImage={bgImg} />
        </article>
      );
    }
  };
};

export const MediaCard = MediaCardHOC();
export default MediaCard;

export const MediaCardNoCheckbox = MediaCardHOC(CardBody(undefined, null, undefined));
